/* eslint-disable react/boolean-prop-naming */
import {useEffect, useState, forwardRef} from 'react';

import ChevronBackIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {createTheme, Divider, useTheme, ThemeProvider, IconButton, Grid} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import {bool, func, string, node, oneOfType, arrayOf, shape} from 'prop-types';

import {FONT_FAMILIES} from '../../const';

const SlideTransition = forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const createDialogTheme = parentTheme => {
  return createTheme({
    ...parentTheme, // spreading parent theme allows to keep all base stuff configured in it (eg: color palette)
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            padding: 1
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingTop: 8,
            paddingBottom: 0
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            width: '50%',
            [parentTheme.breakpoints.down('md')]: {
              width: '80%'
            },
            margin: '0 auto'
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            textAlign: 'center',
            fontSize: 18,
            padding: parentTheme.spacing(1.5),
            fontFamily: FONT_FAMILIES.TITLE,
            [parentTheme.breakpoints.down('md')]: {
              padding: parentTheme.spacing(1),
              fontSize: 17
            }
          }
        }
      }
    }
  });
};

const BaseModal = props => {
  const [isFullScreenEnabled, setIsFullScreenEnabled] = useState(false);

  const {
    hasTransition = false,
    isFullScreen = false,
    customRenderTitle,
    hasFullScreenButton = false,
    shouldMatchEmbedReportWidth = false,
    withoutTitle = false,
    onClose,
    title = '',
    children,
    hasCloseIcon = false,
    hasBackIcon = false,
    canCloseOnBackdropClick = true,
    overridesPaperProps,
    hasTitleDivider = true,
    onBackIconClick,
    headerStyle = {},
    ...rest
  } = props;

  const [reportWidth, setReportWidth] = useState(0);

  const parentTheme = useTheme();
  const dialogTheme = createDialogTheme(parentTheme);

  const shouldApplyReportWidth = shouldMatchEmbedReportWidth && !isFullScreen && !isFullScreenEnabled;
  const paperProps = {
    sx: {
      maxHeight: isFullScreen ? '100%' : 'calc(100% - 8px)',
      ...(!isFullScreenEnabled && !isFullScreen
        ? {
            margin: {
              xs: 1,
              sm: 2,
              md: 3
            }
          }
        : {
            margin: 0
          }),

      width: {
        xs: '100%'
      },
      ...overridesPaperProps,
      ...(shouldApplyReportWidth && {maxWidth: `${reportWidth}px !important`})
    }
  };

  const reportHtmlElement = document.querySelector('.report-embed');

  useEffect(() => {
    if (shouldMatchEmbedReportWidth) {
      setReportWidth(reportHtmlElement?.clientWidth);
    }
  }, [shouldMatchEmbedReportWidth, reportHtmlElement?.clientWidth]);

  // eslint-disable-next-line complexity
  const renderTitle = () => {
    if (withoutTitle) return null;

    if (customRenderTitle) return customRenderTitle();

    if (hasCloseIcon || hasBackIcon || hasFullScreenButton) {
      return (
        <Grid container alignItems="center" sx={{backgroundColor: headerStyle.backgroundColor}}>
          {hasBackIcon ? (
            <Grid item ml={1}>
              <IconButton
                aria-label="close"
                onClick={onBackIconClick}
                sx={{
                  color: theme => theme.palette.grey[500]
                }}
              >
                <ChevronBackIcon />
              </IconButton>
            </Grid>
          ) : null}
          <DialogTitle sx={{flex: 1, color: headerStyle.titleColor, fontSize: `${headerStyle.titleFontSize}px !important`}} ml={hasBackIcon ? 0 : 6}>
            {title}
          </DialogTitle>

          {hasFullScreenButton ? (
            <Grid item mr={1} sx={{cursor: 'pointer'}} onClick={() => setIsFullScreenEnabled(!isFullScreenEnabled)}>
              {isFullScreenEnabled ? <FullscreenExitIcon sx={{fontSize: 30}} /> : <FullscreenIcon sx={{fontSize: 30}} />}
            </Grid>
          ) : null}

          {hasCloseIcon ? (
            <Grid item mr={1}>
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  color: theme => headerStyle.closeIconColor || theme.palette.grey[500]
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          ) : null}

          {hasTitleDivider ? <Divider sx={{my: 0.5, width: '100%'}} /> : null}
        </Grid>
      );
    }

    return (
      <>
        <DialogTitle>{title}</DialogTitle>
        {hasTitleDivider ? <Divider sx={{my: 0.5}} /> : null}
      </>
    );
  };

  const handleClose = (event, reason) => {
    if (!canCloseOnBackdropClick && reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <ThemeProvider theme={dialogTheme}>
      <Dialog
        fullScreen={isFullScreenEnabled || isFullScreen}
        onClose={(e, r) => handleClose(e, r)}
        fullWidth
        PaperProps={paperProps}
        {...(hasTransition ? {TransitionComponent: SlideTransition} : {})}
        {...rest}
      >
        {renderTitle()}
        {children}
      </Dialog>
    </ThemeProvider>
  );
};

BaseModal.propTypes = {
  hasTransition: bool,
  onClose: func.isRequired,
  customRenderTitle: func,
  onBackIconClick: func,
  title: oneOfType([string, node]),
  children: oneOfType([arrayOf(node), node]).isRequired,
  withoutTitle: bool,
  hasCloseIcon: bool,
  hasBackIcon: bool,
  canCloseOnBackdropClick: bool,
  overridesPaperProps: shape({}),
  hasFullScreenButton: bool,
  shouldMatchEmbedReportWidth: bool,
  isFullScreen: bool,
  hasTitleDivider: bool,
  headerStyle: shape({
    backgroundColor: string,
    titleColor: string,
    closeIconColor: string
  })
};

export default BaseModal;
